import { useCallback } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { getOptimalConfig } from '../../lib/calculations';
import { resolvePackageCosts } from '../../lib/calculations';
import { formatCurrencyCompact, formatNumber, formatPercent, formatPrice } from '../../lib/formatters';
import { ASSUMPTION_GAPS } from '../../data/conjointData';
import { Download } from 'lucide-react';

export function ExportSummaryButton() {
  const packages = useSimulatorStore(s => s.packages);
  const assumptions = useSimulatorStore(s => s.assumptions);
  const portfolio = usePortfolioPnL();

  const handleExport = useCallback(() => {
    const optimal = getOptimalConfig(assumptions);
    const pnl = portfolio.portfolioPnLSplit;
    const highRisks = ASSUMPTION_GAPS.filter(g => g.riskLevel === 'HIGH');

    const pkgRows = packages.map((pkg, i) => {
      const contrib = portfolio.packages[i];
      const costs = resolvePackageCosts(pkg, assumptions);
      return `
        <tr>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;font-weight:600">${pkg.name}</td>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb">${pkg.s1OfferName || '—'}</td>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${formatPrice(pkg.price)}</td>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${pkg.enabledFeatures.length}/7</td>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${formatPrice(costs.s1CostPerUnit)}</td>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${formatPercent(costs.s2CloseRate)}</td>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${formatNumber(Math.round(contrib?.s2UnitsSplit ?? 0))}</td>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right;color:${(contrib?.contributionMarginSplit ?? 0) >= 0 ? '#10B981' : '#EF4444'}">${formatCurrencyCompact(contrib?.contributionMarginSplit ?? 0)}</td>
        </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>AlterMe Product Configurator — Executive Summary</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 900px; margin: 40px auto; padding: 0 20px; color: #111827; }
    h1 { font-size: 24px; margin-bottom: 4px; }
    h2 { font-size: 16px; color: #4B5563; margin-top: 32px; border-bottom: 2px solid #E5E7EB; padding-bottom: 8px; }
    .subtitle { color: #6B7280; font-size: 14px; margin-bottom: 24px; }
    .metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 20px 0; }
    .metric { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px; padding: 16px; }
    .metric-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #6B7280; margin-bottom: 4px; }
    .metric-value { font-size: 22px; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
    .positive { color: #10B981; }
    .negative { color: #EF4444; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th { text-align: left; padding: 8px; border-bottom: 2px solid #E5E7EB; color: #6B7280; font-size: 11px; text-transform: uppercase; }
    .risk { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; }
    .risk-high { background: #FEE2E2; color: #DC2626; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #E5E7EB; font-size: 11px; color: #9CA3AF; }
    @media print { body { margin: 20px; } }
  </style>
</head>
<body>
  <h1>AlterMe Product Configurator</h1>
  <p class="subtitle">Executive Summary — ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

  <h2>Portfolio Overview</h2>
  <div class="metrics">
    <div class="metric">
      <div class="metric-label">Monthly P&L</div>
      <div class="metric-value ${pnl >= 0 ? 'positive' : 'negative'}">${formatCurrencyCompact(pnl)}</div>
    </div>
    <div class="metric">
      <div class="metric-label">Annual P&L (run-rate)</div>
      <div class="metric-value ${pnl >= 0 ? 'positive' : 'negative'}">${formatCurrencyCompact(pnl * 12)}</div>
    </div>
    <div class="metric">
      <div class="metric-label">Total S2 Units/Mo</div>
      <div class="metric-value">${formatNumber(Math.round(portfolio.totalS2UnitsSplit))}</div>
    </div>
    <div class="metric">
      <div class="metric-label">Contribution Margin</div>
      <div class="metric-value">${(portfolio.contributionMarginPctSplit * 100).toFixed(1)}%</div>
    </div>
  </div>
  <p style="font-size:12px;color:#6B7280">Split demand model with ${((assumptions.cannibalizationRate ?? 0) * 100).toFixed(0)}% cannibalization. Fixed costs ($${assumptions.fixedCosts.toLocaleString()}/mo) applied once across ${packages.length} packages.</p>

  <h2>Package Configuration</h2>
  <table>
    <thead>
      <tr>
        <th>Package</th>
        <th>S1 Offer</th>
        <th style="text-align:right">S2 Price</th>
        <th style="text-align:right">Features</th>
        <th style="text-align:right">S1 Cost</th>
        <th style="text-align:right">Close Rate</th>
        <th style="text-align:right">S2 Units</th>
        <th style="text-align:right">Contribution</th>
      </tr>
    </thead>
    <tbody>
      ${pkgRows}
    </tbody>
  </table>

  <h2>Best Single-Product Scenario</h2>
  <p style="font-size:13px">Optimal standalone configuration: <strong>${formatPrice(optimal.price)}</strong> with <strong>${optimal.featureCount} features</strong> (${optimal.features.join(', ')}). Standalone P&L: <strong>${formatCurrencyCompact(optimal.monthlyPnL)}</strong>/mo.</p>

  ${highRisks.length > 0 ? `
  <h2>Key Risks</h2>
  <table>
    <thead><tr><th>Metric</th><th>ProForma</th><th>Conjoint</th><th>Gap</th><th>Risk</th></tr></thead>
    <tbody>
      ${highRisks.map(r => `<tr>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;font-weight:600">${r.metric}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;font-size:12px">${r.financialModel}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;font-size:12px">${r.conjointEvidence}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;font-size:12px">${r.gap}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb"><span class="risk risk-high">HIGH</span></td>
      </tr>`).join('')}
    </tbody>
  </table>` : ''}

  <h2>Key Assumptions</h2>
  <table>
    <thead><tr><th>Parameter</th><th style="text-align:right">Value</th></tr></thead>
    <tbody>
      <tr><td style="padding:6px;border-bottom:1px solid #e5e7eb">Monthly S1 Leads</td><td style="padding:6px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace">${formatNumber(assumptions.s1Leads)}</td></tr>
      <tr><td style="padding:6px;border-bottom:1px solid #e5e7eb">Monthly Fixed Costs</td><td style="padding:6px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace">${formatPrice(assumptions.fixedCosts)}</td></tr>
      <tr><td style="padding:6px;border-bottom:1px solid #e5e7eb">Total Deduction Rate</td><td style="padding:6px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace">${formatPercent(assumptions.totalDeductionRate)}</td></tr>
      <tr><td style="padding:6px;border-bottom:1px solid #e5e7eb">Feature Scaling</td><td style="padding:6px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace">${assumptions.featureScaling}</td></tr>
      <tr><td style="padding:6px;border-bottom:1px solid #e5e7eb">Cannibalization Rate</td><td style="padding:6px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace">${formatPercent(assumptions.cannibalizationRate ?? 0)}</td></tr>
    </tbody>
  </table>

  <div class="footer">
    <p>Generated from AlterMe Product Configurator · Source: CBC Conjoint Study, n=1,001 · ${new Date().toISOString()}</p>
    <p>Scenario projections based on conjoint cold-market demand. Not a sales forecast. Actual results depend on operational execution.</p>
  </div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onload = () => {
        setTimeout(() => win.print(), 500);
      };
    }
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }, [packages, assumptions, portfolio]);

  return (
    <button
      onClick={handleExport}
      className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border border-border text-secondary hover:text-primary hover:bg-surface transition-colors"
    >
      <Download size={14} />
      Export Summary
    </button>
  );
}
